import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Alert } from '../ui';
import { useSalon } from '../../contexts/SalonContext';
import { useAppointments } from '../../contexts/AppointmentContext';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, query, where, orderBy } from 'firebase/firestore';
import { useAvailability } from '../../hooks/useAvailability';

import MobileServiceSelection from '../mobile/MobileServiceSelection';
import MobileDateTimeSelection from '../mobile/MobileDateTimeSelection';
import MobileClientForm from '../mobile/MobileClientForm';
import MobileBookingSummary from '../mobile/MobileBookingSummary';
import { formatarPreco } from '../../utils/priceUtils';
import { formatWhatsApp } from '../../utils/formatUtils';
import { isValidDateBR, formatDateBR, convertDateBRToISO } from '../../utils/dateUtils';
import { getCategories } from '../../firebase/salon';
import { formatAppointmentDuration } from '../../utils/performanceUtils';

const AdminMobileAppointmentForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  services, 
  professionals,
  salonId,
  initialData = null,
  mode = 'new', // 'new', 'reschedule', 'editServices'    
  hideResume = false
}) => {
  // Estado para controlar a etapa atual do formulário
  const [currentStep, setCurrentStep] = useState(mode === 'reschedule' ? 2 : (mode === 'editServices' ? 1 : 1));
  
  // Estado para formulário
  const [formData, setFormData] = useState({
    cliente: '',
    whatsapp: '',
    email: '',
    data: '',
    dataISO: '',
    horario: '',
    servicos: [],
    profissional: '',
    confirmado: false,
    pagamentoConfirmado: false
  });
  
  // Estado para feedback ao usuário
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Estado para categorias (pode ser carregado do backend)
  const [categories, setCategories] = useState([]);
  
  // Refs para controlar estados de carregamento e prevenir loops infinitos
  const categoriesLoaded = useRef(false);
  const initialDataLoaded = useRef(false);
  const availabilityChecked = useRef(false);
  const stepChangeInProgress = useRef(false);
  const wasOpenBefore = useRef(false); // Ref para rastrear se o modal já estava aberto
  
  // Usar hook de disponibilidade centralizado
  const { 
    getTimeSlotsForServices, 
    preloadAvailability,
    checkSlotAvailability,
    services: allServices
  } = useAvailability(salonId);

  // Função auxiliar para extrair categorias dos serviços
  const extractCategoriesFromServices = useCallback((servicesList) => {
    if (!servicesList || !servicesList.length) return [];

    const categoriesMap = {};
    
    servicesList.forEach(service => {
      if (service.categoria) {
        if (!categoriesMap[service.categoria]) {
          // Para cada categoria única, criamos um objeto com ID e nome
          categoriesMap[service.categoria] = {
            id: service.categoria,
            nome: service.categoriaNome || 'Categoria',
            ordem: service.categoriaOrdem || 0
          };
        }
      }
    });
    
    return Object.values(categoriesMap);
  }, []);

  // Função para limpar o formulário
  const resetForm = useCallback(() => {
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0]; // ISO format YYYY-MM-DD
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    setFormData({
      cliente: '',
      whatsapp: '',
      email: '',
      data: currentDate,
      dataISO: currentDate,
      dataFormatada: formatDateBR(currentDate),
      horario: currentTime,
      servicos: [],
      profissional: '',
      confirmado: false,
      pagamentoConfirmado: false
    });
    
    setCurrentStep(1);
    setError(null);
    setSuccess(false);
    
    // Resetar flags de controle
    categoriesLoaded.current = false;
    initialDataLoaded.current = false;
    availabilityChecked.current = false;
  }, []);

  // Resetar o formulário quando o modal é aberto e preencher com dados iniciais se fornecidos
  useEffect(() => {
    if (!isOpen) {
      // Quando o modal fechar, resetar o flag
      wasOpenBefore.current = false;
      return;
    }
    
    // Se o modal já estava aberto antes, não fazer nada
    // Isso previne que a seleção de profissional cause um reset
    if (wasOpenBefore.current) {
      return;
    }
    
    wasOpenBefore.current = true;
    
    // Resetar flags de controle
    initialDataLoaded.current = false;
    availabilityChecked.current = false;
    
    if (initialData && !initialDataLoaded.current) {
      // Garantir que a data esteja no formato correto
      let dataValue = initialData.data || '';
      
      // Se a data estiver no formato brasileiro, converter para ISO
      if (dataValue && dataValue.includes('/')) {
        dataValue = convertDateBRToISO(dataValue);
      }
      
      // Preencher com dados existentes
      setFormData({
        ...initialData,
        cliente: initialData.cliente || '',
        whatsapp: initialData.whatsapp || '',
        email: initialData.email || '',
        data: dataValue,
        dataISO: dataValue,
        dataFormatada: formatDateBR(dataValue),
        horario: initialData.horario || '',
        servicos: initialData.servicos || [],
        profissional: initialData.profissional || '',
        confirmado: initialData.confirmado || false,
        pagamentoConfirmado: initialData.pagamentoConfirmado || false
      });
      
      initialDataLoaded.current = true;
      
      // Pré-carregar disponibilidade uma única vez se temos data e profissional
      if (dataValue && initialData.profissional && !availabilityChecked.current) {
        const profId = typeof initialData.profissional === 'object'
          ? initialData.profissional.id
          : initialData.profissional;
          
        preloadAvailability(dataValue, dataValue, profId);
        availabilityChecked.current = true;
      }
    } else if (!initialData) {
      resetForm();
    }
    
    // Definir etapa inicial baseada no modo
    setCurrentStep(mode === 'reschedule' ? 2 : (mode === 'editServices' ? 1 : 1));
    setError(null);
    setSuccess(false);
  }, [isOpen, initialData, mode, preloadAvailability, resetForm]);

  // Efeito para carregar categorias quando o modal é aberto
  useEffect(() => {
    if (!isOpen || !salonId || categoriesLoaded.current) return;
    
    const loadCategories = async () => {
      try {
        setLoading(true);
        // Carregar as categorias diretamente do Firestore em vez de extrair dos serviços
        const categoriesData = await getCategories(salonId);
        
        if (categoriesData && categoriesData.length > 0) {
          // Usar as categorias carregadas diretamente do banco
          setCategories(categoriesData);
        } else {
          // Fallback: extrair categorias dos serviços se não houver dados no Firestore
          const categoriesFromServices = extractCategoriesFromServices(services);
          setCategories(categoriesFromServices);
        }
        
        categoriesLoaded.current = true;
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        // Fallback: extrair categorias dos serviços em caso de erro
        const categoriesFromServices = extractCategoriesFromServices(services);
        setCategories(categoriesFromServices);
        categoriesLoaded.current = true;
      } finally {
        setLoading(false);
      }
    };
    
    loadCategories();
  }, [isOpen, salonId, services, extractCategoriesFromServices]);

  // Handler para seleção de serviços
  const handleServiceSelect = useCallback((serviceId) => {
    setFormData(prev => {
      const servicos = prev.servicos.includes(serviceId)
        ? prev.servicos.filter(id => id !== serviceId)
        : [...prev.servicos, serviceId];
      return {
        ...prev,
        servicos
      };
    });
  }, []);

  // Handler para seleção de data
  const handleDateSelect = useCallback((date) => {
    // Garantir que a data está no formato ISO (YYYY-MM-DD)
    let isoDate = date;
    if (date && date.includes('/')) {
      // Se estiver no formato brasileiro, converter para ISO
      isoDate = convertDateBRToISO(date);
    }
    
    setFormData(prev => ({
      ...prev,
      data: isoDate, // Formato ISO
      dataISO: isoDate, // Garantir que dataISO esteja definido
      dataFormatada: formatDateBR(isoDate) // Formato brasileiro para exibição
    }));
    
    // Resetar flag para forçar verificação de disponibilidade
    availabilityChecked.current = false;
  }, []);

  // Efeito para pré-carregar disponibilidade quando data ou profissional mudam
  useEffect(() => {
    if (!formData.data || !formData.profissional || availabilityChecked.current) return;
    
    const profId = typeof formData.profissional === 'object'
      ? formData.profissional.id
      : formData.profissional;
    
    preloadAvailability(formData.data, formData.data, profId);
    availabilityChecked.current = true;
  }, [formData.data, formData.profissional, preloadAvailability]);

  // Handler para seleção de horário
  const handleTimeSelect = useCallback((time) => {
    setFormData(prev => ({
      ...prev,
      horario: time
    }));
  }, []);

  // Efeito para verificar disponibilidade quando horário, data, ou profissional mudam
  useEffect(() => {
    if (!formData.profissional || !formData.data || !formData.horario) return;
    
    const profId = typeof formData.profissional === 'object'
      ? formData.profissional.id
      : formData.profissional;
    
    const checkTimeAvailability = async () => {
      try {
        const availability = await checkSlotAvailability(
          formData.data,
          formData.horario,
          profId,
          formData.servicos
        );
        
        // Se não está disponível e não permite sobreposição, limpar seleção
        if (!availability.available && !availability.allowsOverlap) {
          setFormData(prev => ({
            ...prev,
            profissional: ''
          }));
          
          setError(`O profissional selecionado não está disponível neste horário. Por favor, escolha outro profissional.`);
          setTimeout(() => setError(null), 5000);
        }
      } catch (error) {
        console.error('Erro ao verificar disponibilidade:', error);
      }
    };
    
    checkTimeAvailability();
  }, [formData.horario, formData.data, formData.profissional, formData.servicos, checkSlotAvailability]);

  // Handler para seleção de profissional
  const handleProfessionalSelect = useCallback((profId) => {
    setFormData(prev => ({
      ...prev,
      profissional: profId
    }));
    
    // Resetar flag para forçar nova verificação de disponibilidade
    availabilityChecked.current = false;
  }, []);

  // Função para avançar para a próxima etapa
  const handleNext = useCallback(() => {
    // Evitar mudanças de etapa concorrentes
    if (stepChangeInProgress.current) {
      return;
    }
    
    stepChangeInProgress.current = true;
    
    // No modo 'editServices', após selecionar os serviços, enviar o formulário
    if (mode === 'editServices' && currentStep === 1) {
      handleSubmit();
      stepChangeInProgress.current = false;
      return;
    }
    
    // No modo 'reschedule', após selecionar data e profissional, enviar o formulário
    if (mode === 'reschedule' && currentStep === 2) {
      handleSubmit();
      stepChangeInProgress.current = false;
      return;
    }
    
    setCurrentStep(prev => prev + 1);
    
    stepChangeInProgress.current = false;
  }, [currentStep, mode]);

  // Função para voltar para a etapa anterior
  const handleBack = useCallback(() => {
    // Evitar mudanças de etapa concorrentes
    if (stepChangeInProgress.current) {
      return;
    }
    
    stepChangeInProgress.current = true;
    
    if (currentStep > 1) {
      // No modo 'reschedule' ou 'editServices', fechar o formulário ao clicar em voltar na primeira etapa
      if ((mode === 'reschedule' || mode === 'editServices') && currentStep === 1) {
        onClose();
        stepChangeInProgress.current = false;
        return;
      }
      
      setCurrentStep(prev => prev - 1);
    } else {
      onClose();
    }
    
    stepChangeInProgress.current = false;
  }, [currentStep, mode, onClose]);

  // Função para verificar disponibilidade utilizando o hook centralizado
  const checkAvailability = useCallback(async (date, time, professionalId, serviceIds, excludeAppointmentId = null) => {
    try {
      // Utilizar o método do hook centralizado
      const availability = await checkSlotAvailability(
        date,
        time,
        professionalId,
        serviceIds,
        excludeAppointmentId
      );
      
      return availability;
    } catch (error) {
      // Retornar objeto padrão em caso de erro
      return {
        available: false,
        allowsOverlap: false,
        conflicts: [],
        hasConflicts: false
      };
    }
  }, [checkSlotAvailability]);

  // Handler para formulário de cliente
  const handleClientFormSubmit = useCallback((clientData) => {
    // Em vez de atualizar o estado e depois chamar handleSubmit,
    // vamos usar os dados diretamente
    const updatedFormData = {
      ...formData,
      ...clientData
    };
    
    // Atualizamos o estado
    setFormData(updatedFormData);
    
    // E usamos os dados atualizados diretamente
    handleSubmitWithData(updatedFormData);
  }, [formData]);
  
  // Nova função que recebe os dados diretamente
  const handleSubmitWithData = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      // Em modo de edição de serviços, submeter apenas os serviços
      if (mode === 'editServices') {
        if (data.servicos.length === 0) {
          setError('Por favor, selecione pelo menos um serviço');
          setLoading(false);
          return;
        }
        
        console.log('DEBUG_SUBMIT: Enviando edição de serviços');
        onSubmit({
          ...initialData,
          servicos: data.servicos
        });
        
        setSuccess(true);
        // Não fechar automaticamente
        return;
      }
      
      // Em modo de remarcação, submeter apenas data, horário e profissional
      if (mode === 'reschedule') {
        if (!data.data || !data.horario || !data.profissional) {
          setError('Por favor, preencha todos os campos obrigatórios');
          setLoading(false);
          return;
        }
        
        try {
          // Verificar disponibilidade do horário usando o hook centralizado
          const profId = typeof data.profissional === 'object'
            ? data.profissional.id
            : data.profissional;
            
          console.log('DEBUG_SUBMIT: Verificando disponibilidade para remarcação');
          const availability = await checkAvailability(
            data.data,
            data.horario,
            profId,
            data.servicos,
            initialData?.id // excluir o agendamento atual da verificação
          );
          
          if (!availability.available && !availability.allowsOverlap) {
            setError(`Este horário já está ocupado. Por favor, escolha outro horário.`);
            setLoading(false);
            return;
          }
          
          // Se há conflitos mas permite sobreposição, adicionar aviso
          if (availability.hasConflicts && availability.allowsOverlap) {
            data.temConflitos = true;
            data.conflitos = availability.conflicts;
          }
        } catch (availabilityError) {
          console.error('Erro ao verificar disponibilidade:', availabilityError);
          // Permitir continuar mesmo com erro na verificação
        }
        
        // Garantir formato de data correto
        const dataToSubmit = data.data;
        
        console.log('DEBUG_SUBMIT: Enviando remarcação');
        onSubmit({
          ...initialData,
          data: dataToSubmit,
          dataISO: dataToSubmit,
          dataFormatada: formatDateBR(dataToSubmit),
          horario: data.horario,
          profissional: data.profissional
        });
        
        setSuccess(true);
        setCurrentStep(4); // Avançar para tela de confirmação em vez de fechar
        return;
      }
      
      // Validações para agendamento completo
      if (!data.cliente || 
          !data.whatsapp || 
          !data.data || 
          !data.horario || 
          !data.profissional || 
          data.servicos.length === 0) {
        
        // Adicionando logs detalhados para debug
        console.log('DEBUG_SUBMIT: Validação falhou. Detalhes:');
        console.log('cliente:', data.cliente);
        console.log('whatsapp:', data.whatsapp);
        console.log('data:', data.data);
        console.log('horario:', data.horario);
        console.log('profissional:', data.profissional);
        console.log('servicos:', data.servicos);
        console.log('formData completo:', data);
        
        setError('Por favor, preencha todos os campos obrigatórios');
        setLoading(false);
        return;
      }
      
      // Validar formato do WhatsApp
      if (data.whatsapp.replace(/\D/g, '').length < 10) {
        setError('Por favor, digite um número de WhatsApp válido');
        setLoading(false);
        return;
      }
      
      try {
        // Verificar disponibilidade do horário para novos agendamentos
        const profId = typeof data.profissional === 'object'
          ? data.profissional.id
          : data.profissional;
        
        console.log('DEBUG_SUBMIT: Verificando disponibilidade para novo agendamento');  
        const availability = await checkAvailability(
          data.data,
          data.horario,
          profId,
          data.servicos
        );
        
        if (!availability.available && !availability.allowsOverlap) {
          setError(`Este horário já está ocupado. Por favor, escolha outro horário.`);
          setLoading(false);
          return;
        }
        
        // Se há conflitos mas permite sobreposição, adicionar aviso
        if (availability.hasConflicts && availability.allowsOverlap) {
          data.temConflitos = true;
          data.conflitos = availability.conflicts;
        }
      } catch (availabilityError) {
        console.error('Erro ao verificar disponibilidade:', availabilityError);
        // Permitir continuar mesmo com erro na verificação
      }
      
      // Chama a função onSubmit passada via props
      console.log('DEBUG_SUBMIT: Enviando novo agendamento');
      onSubmit(data);
      setSuccess(true);
      
      // Avançar para tela de sucesso
      setCurrentStep(4);
    } catch (error) {
      console.error('Erro ao submeter formulário:', error);
      setError('Erro ao processar o agendamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  // Função para submeter o formulário usando o estado atual
  const handleSubmit = () => {
    handleSubmitWithData(formData);
  };

  // Handler para iniciar novo agendamento
  const handleNewBooking = useCallback(() => {
    resetForm();
    setCurrentStep(1);
  }, [resetForm]);

  // Se o modal não estiver aberto, não renderiza nada
  if (!isOpen) return null;

  // Funções de renderização baseadas na etapa atual
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: // Serviços
        return (
          <MobileServiceSelection
            services={services}
            categories={categories}
            selectedServices={formData.servicos}
            onServiceSelect={handleServiceSelect}
            onNext={handleNext}
            loading={loading}
            error={error}
          />
        );
      case 2: // Data, Hora e Profissional
        return (
          <MobileDateTimeSelection
            selectedServices={formData.servicos}
            professionals={professionals}
            availableSlots={[]}
            selectedDate={formData.data}
            selectedTime={formData.horario}
            selectedProfessional={formData.profissional}
            onDateSelect={handleDateSelect}
            onTimeSelect={handleTimeSelect}
            onProfessionalSelect={handleProfessionalSelect}
            onNext={handleNext}
            onBack={handleBack}
            loading={loading}
            error={error}
          />
        );
      case 3: // Formulário de Cliente
        return (
          <MobileClientForm
            onSubmit={handleClientFormSubmit}
            onBack={handleBack}
            loading={loading}
            error={error}
            initialData={formData}
            selectedServices={formData.servicos}
            selectedDate={formData.data}
            selectedTime={formData.horario}
            selectedProfessional={formData.profissional}
            services={services}
            professionals={professionals}
          />
        );
      case 4: // Resumo/Confirmação
        return (
          <MobileBookingSummary
            success={success}
            appointmentData={formData}
            services={services}
            professionals={professionals}
            onNewBooking={handleNewBooking}
            onClose={onClose}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white" style={{ paddingTop: 'env(safe-area-inset-top)', minHeight: 'calc(44px + env(safe-area-inset-top))' }}>
        <button 
          onClick={handleBack}
          className="text-gray-700"
        >
          {currentStep === 1 ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          )}
        </button>
        <h1 className="text-lg font-semibold">
          {currentStep === 1 && (mode === 'editServices' ? "Editar Serviços" : "Selecionar Serviços")}
          {currentStep === 2 && (mode === 'reschedule' ? "Remarcação" : "Data e Profissional")}
          {currentStep === 3 && "Dados do Cliente"}
          {currentStep === 4 && "Confirmação"}
        </h1>
        <div className="w-6"></div> {/* Espaçador para centralizar o título */}
      </div>

      {/* Indicador de progresso */}
      <div className="w-full flex px-2 mt-1">
        {Array.from({ length: currentStep === 4 ? 3 : 3 }).map((_, index) => (
          <div 
            key={index} 
            className={`h-1 flex-1 mx-0.5 rounded-full ${
              index + 1 <= currentStep ? 'bg-blue-500' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Conteúdo */}
      <div className={`flex-1 overflow-y-auto p-4 ${formData.servicos.length > 0 ? 'pb-28' : 'pb-4'}`}>
        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default AdminMobileAppointmentForm; 